/**
 * MACRO #32 — Founding Territory provenance section.
 *
 * Presents the Genesis provenance that belongs to the TERRITORY: its canonical
 * Genesis ordinal (or provisional/pending state) and the first canonical
 * acquisition event (block / log index / tx), plus an explicit statement that
 * provenance stays with the Territory across sales while current ownership is
 * reported separately. Renders nothing for a non-Genesis parcel.
 */
import { GenesisBadge } from "./GenesisBadge";
import { useGenesisParcel } from "./useGenesis";

function short(a: string | null | undefined): string {
  if (!a) return "—";
  const s = String(a);
  return s.length > 14 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
}

export function FoundingProvenance({ parcelId }: { parcelId: number | string | null | undefined }) {
  const { member } = useGenesisParcel(parcelId);
  if (!member || !member.is_genesis) return null;

  const fa = member.first_acquisition;
  const finalized = member.set_status === "FINALIZED" && member.genesis_ordinal != null;

  return (
    <section data-testid="founding-provenance" className="mt-12">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display text-lg font-semibold text-text-primary uppercase tracking-[0.02em]">
          Founding Territory
        </h2>
        <GenesisBadge member={member} testId="founding-provenance-badge" />
      </div>

      <div className="neon-glass rounded-lg border border-[#F5C451]/25 p-5 space-y-4">
        <p className="font-mono text-[11px] leading-relaxed text-text-secondary">
          This is a <span className="text-[#F5C451]">Founding Territory</span> of THE GRID. Its Genesis
          provenance belongs to the Territory itself and is preserved across every future sale — the
          current owner shown below is reported separately and does not acquire founder status.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <ProvStat label="Genesis" testId="prov-ordinal">
            {finalized ? `#${member.genesis_ordinal}` : member.preview_ordinal != null ? `#${member.preview_ordinal}*` : "Pending"}
          </ProvStat>
          <ProvStat label="Status" testId="prov-status">
            {member.set_status === "FINALIZED" ? "Finalized" : "Draft"}
          </ProvStat>
          <ProvStat label="First Acq. Block" testId="prov-block">
            {fa && fa.block_number != null ? fa.block_number.toLocaleString("en-US") : "—"}
          </ProvStat>
          <ProvStat label="Log Index" testId="prov-log">
            {fa && fa.log_index != null ? String(fa.log_index) : "—"}
          </ProvStat>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <ProvStat label="First Acquisition Tx" testId="prov-tx">
            {fa?.tx_hash ? short(fa.tx_hash) : "—"}
          </ProvStat>
          <ProvStat label="Current Owner (not founder)" testId="prov-owner">
            {short(member.current_owner)}
          </ProvStat>
        </div>

        {!finalized && member.preview_ordinal != null && (
          <p data-testid="prov-provisional-note" className="font-mono text-[9.5px] text-text-muted tracking-[0.1em]">
            * Provisional ordinal — becomes definitive only when the Genesis set is finalized.
          </p>
        )}
      </div>
    </section>
  );
}

function ProvStat({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-text-muted mb-1">{label}</div>
      <div data-testid={testId} className="font-display text-sm text-text-primary tabular-nums break-all">
        {children}
      </div>
    </div>
  );
}
